import React, { useEffect, useState, useContext } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { UserContext } from '../contexts/UserContext';
import { useSubscription } from '../hooks/useSubscription';

/**
 * SubscriptionGuard component handles post-login redirects based on subscription status
 *
 * Redirect Logic:
 * - No subscriptions -> /settings (subscriptions tab)
 * - Any subscription -> /admin (Dashboard is always the default landing page)
 *
 * Dashboard is always accessible and will show content based on active subscriptions.
 */
const SubscriptionGuard = ({ children }) => {
  const { user } = useContext(UserContext);
  const navigate = useNavigate();
  const location = useLocation();
  const [hasRedirected, setHasRedirected] = useState(false);

  // Użyj centralnego kontekstu subskrypcji
  const { loading, hasAnySubscription } = useSubscription();

  // Handle redirection based on subscription status
  useEffect(() => {
    if (loading || hasRedirected || !user?.id) return;

    // Only redirect if we're on /admin path (post-login default)
    if (location.pathname !== '/admin') return;

    // If no subscriptions at all, redirect to settings
    if (!hasAnySubscription()) {
      console.log('SubscriptionGuard: No subscriptions, redirecting to /settings');
      setHasRedirected(true);
      navigate('/settings', { replace: true });
    }
    // Otherwise stay on /admin (Dashboard)
  }, [loading, location.pathname, navigate, hasRedirected, user?.id, hasAnySubscription]);

  // Show loading while checking subscriptions
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Checking subscription status...</p>
        </div>
      </div>
    );
  }

  return children;
};

export default SubscriptionGuard;
